import { guid } from '@fry/lib/utils';


export class EditorEligibilityOperator {
  id: string;
  name: string;
  affirmative: boolean;
  function: 'and' | 'or' | null;
  hideNestedDescriptions: boolean;
  ancestorHideNestedDescriptions: boolean;
  parent: EditorEligibilityOperator | null;
  children: EditorEligibilityOperator[];
  ruleIndex: number | null;

  constructor(obj?: any) {
    this.id = obj?.id ?? null;
    this.name = obj?.name ?? null;
    this.affirmative = obj?.affirmative ?? true;
    this.function = obj?.function ?? null;
    this.hideNestedDescriptions = obj?.hideNestedDescriptions ?? false;
    this.ancestorHideNestedDescriptions = obj?.ancestorHideNestedDescriptions ?? false;
    this.parent = obj?.parent ?? null;
    this.children = obj?.children ?? [];
    this.ruleIndex = obj?.ruleIndex ?? null;
  }

  static createDefault(): EditorEligibilityOperator {
    return new EditorEligibilityOperator({
      id: guid()
    });
  }

  createCopy(): EditorEligibilityOperator {
    return new EditorEligibilityOperator({
      id: this.id,
      name: this.name,
      affirmative: this.affirmative,
      function: this.function,
      hideNestedDescriptions: this.hideNestedDescriptions,
      ancestorHideNestedDescriptions: this.ancestorHideNestedDescriptions,
      parent: this.parent,
      children: [...this.children],
      ruleIndex: this.ruleIndex
    });
  }

  createCompleteCopy(): EditorEligibilityOperator {
    const thisCopy = this.createCopy();
    thisCopy.children = thisCopy.children.map(child => child.createCompleteCopy());
    thisCopy.children.forEach(child => child.parent = thisCopy);
    return thisCopy;
  }

  createClone(): EditorEligibilityOperator {
    const clonedOperator = this.createCopy();
    clonedOperator.id = guid();
    return clonedOperator;
  }

  createCompleteClone(): EditorEligibilityOperator {
    const thisClone = this.createClone();
    thisClone.children = thisClone.children.map(child => child.createCompleteClone());
    thisClone.children.forEach(child => child.parent = thisClone);
    return thisClone;
  }

  getOperatorById(operatorId: string): EditorEligibilityOperator | undefined {
    if (this.id === operatorId) {
      return this;
    }
    for (const child of this.children) {
      const operator = child.getOperatorById(operatorId);
      if (operator !== undefined) {
        return operator;
      }
    }
    return undefined;
  }

  getFirstHasRuleOperator(): EditorEligibilityOperator {
    if (this.hasRule()) {
      return this;
    }
    return this.children[0].getFirstHasRuleOperator();
  }

  getLastHasRuleOperator(): EditorEligibilityOperator {
    if (this.hasRule()) {
      return this;
    }
    return this.children[this.children.length - 1].getLastHasRuleOperator();
  }

  hasRule(): boolean {
    return this.ruleIndex !== null;
  }

  incrementRuleIndexFrom(ruleIndex: number, increment: number = 1): void {
    if (this.hasRule() && this.ruleIndex >= ruleIndex) {
      this.ruleIndex += increment;
    }
    this.children.forEach(child => child.incrementRuleIndexFrom(ruleIndex, increment));
  }

  isShown() {
    return !this.ancestorHideNestedDescriptions && (this.hideNestedDescriptions || this.children.length === 0);
  }
}
